
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import { GetServerSideProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { getSession, useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { ToastContainer } from 'react-toastify'

import { orderItem } from '@/api/order/orderItem'
import { getFaq } from '@/api/other/getFaq'
import { getHostingPlans } from '@/api/web-hosting/getHostingPlans'
import FaqSection from '@/components/faq/FaqSection'
import CmsSection from '@/components/hosting/CmsSection'
import FreeAllTariffs from '@/components/hosting/FreeAllTariffs'
import HostCard from '@/components/hosting/HostCard'
import HostingIspManager from '@/components/hosting/HostingIspManager'
import HostingOnFast from '@/components/hosting/HostingOnFast'
import BaseLayout from '@/components/layouts/BaseLayout'
import Navbar from '@/components/ui/Navbar'
import { servicesSlug } from '@/res/consts'
import { NAMESPACE_HOSTING } from '@/res/namespaces'
import {
  ROUTE_IDENTIFICATION,
  ROUTE_LOGIN,
  ROUTE_ORDER,
  ROUTE_SETTINGS,
} from '@/res/routes'
import { IFaqDetail } from '@/types/Common'
import { IFreeTrialPopup, IHostingPlan } from '@/types/Hosting'
import { getHeaderToken } from '@/utils/getHeaderToken'

const FreeTrialConfirmationPopup = dynamic(
  () => import('@/components/ui/Popups/FreeTrialConfirmationPopup')
)

interface Props {
  plans: IHostingPlan[]
}

const WebHostingPage: NextPage<Props> = ({ plans }) => {
  const { t, lang } = useTranslation(NAMESPACE_HOSTING)
  const [faqs, setFaqs] = useState<IFaqDetail[]>([])
  const [freeTrial, setFreeTrial] = useState<null | IFreeTrialPopup>(null)

  const { data } = useSession()
  const router = useRouter()

  const onSelectPlan = async (id: number) => {
    if (!data?.user) {
      router.push(
        {
          pathname: ROUTE_LOGIN,
          query: {
            from: router.asPath,
          },
        },
        undefined
      )
    } else if (!data.user.is_passport_verified) {
      router.push({
        pathname: ROUTE_IDENTIFICATION,
        query: {
          show_info: true,
        },
      })
    } else if (!data?.user.phone_number) {
      router.push({
        pathname: ROUTE_SETTINGS,
        query: {
          show_info: true,
        },
      })
    } else {
      await orderItem({ plan: id }, data.user.accessToken)
      router.push({
        pathname: ROUTE_ORDER,
        query: {
          plan_id: id,
        },
      })
    }

    return id
  }

  useEffect(() => {
    const _getFaq = async () => {
      const response = await getFaq(servicesSlug.hosting, lang as string)
      setFaqs(response)
    }
    _getFaq()
  }, [lang])

  const onFreeTrialClick = (info: IFreeTrialPopup) => {
    if (!data?.user) {
      router.push({
        pathname: ROUTE_LOGIN,
        query: {
          from: router.asPath,
        },
      })
    } else {
      setFreeTrial(info)
    }
  }

  const onFreeTrialClose = () => {
    setFreeTrial(null)
  }

  return (
    <BaseLayout
      additionalMetaTags={[
        {
          content: keywords.join(','),
          name: 'keywords',
        },
      ]}
      seoTitle={t('seo_title')}
      seoDescription={t('seo_description')}
    >
      {freeTrial ? (
        <FreeTrialConfirmationPopup
          info={freeTrial}
          onClose={onFreeTrialClose}
        />
      ) : null}
      <Navbar notShowMenuIcon />
      <div className="items-center justify-center px-12 mt-24 text-center">
        <ToastContainer />
        <p className="hr:text-[45px] desktop:text-[42px] laptop:text-[40px] tablet:text-[38px] small:text-[34px] text-[28px] small:leading-10 leading-8  font-bold small:px-10 mb-4">
          {t('web_hosting')}
        </p>
        <div className="hr:text-[30px] desktop:text-[26px] laptop:text-xl tablet:text-md small:text-[18px] text-sm text-[#878D9C] ">
          {t('number_one_in_uzb')}
        </div>
      </div>
      <div className="bg-[#F8F9FD] my-12 w-full tablet:py-14 small:py-10 py-8">
        <p className="container hr:text-[24px] hr:leading-10 desktop:text-[21px] laptop:text-[20px] tablet:text-[19px] small:text-[18px] text-base  text-[#767B88] text-center items-start  ">
          {t('optimal_price')}
        </p>
        <div className="container grid grid-cols-1 gap-3 mt-10 desktop:gap-5 tablet:mt-14 small:mt-12 hr:px-10 desktop:grid-cols-3 small:grid-cols-2">
          {plans.map((plan) => (
            <HostCard
              name={plan.name}
              price={parseFloat(plan.price)}
              key={plan.id}
              isPopular={plan.is_popular}
              alternative={plan.attributes.alternative}
              mailboxes={plan.attributes.mailboxes}
              second_level={plan.attributes.second_level}
              subdomains={plan.attributes.subdomains}
              ssd={plan.attributes.disk}
              db={plan.attributes.db}
              id={plan.id}
              hasTrial={plan.has_trial_period}
              onSelectPlan={onSelectPlan}
              onFreeTrialClick={onFreeTrialClick}
            />
          ))}
        </div>
      </div>
      <FreeAllTariffs />
      <CmsSection />
      <HostingOnFast />
      <HostingIspManager />
      <FaqSection faq_info={faqs} />
    </BaseLayout>
  )
}

 const _getServerSideProps: GetServerSideProps = async (ctx) => {
  const session = await getSession({ ctx })
  let headers: any = {}

  if (session?.user) {
    headers = { ...getHeaderToken(session.user.accessToken) }
  }
  const res = await getHostingPlans(headers)

  return {
    props: {
      plans: res,
    },
  }
}

const keywords = [
  'Web hosting uz',
  'Hosting Uzbekistan',
  'Хостинг уз',
  'Купить хостинг',
  'Дешевый хостинг уз',
  'Hosting uz',
  'Whois uz',
  'Сколько стоит хостинг',
  'Хостинг для сайтов',
]

export default WebHostingPage


// @ts-ignore
    export async function getServerSideProps(ctx) {
// @ts-ignore
        let res = _getServerSideProps(ctx)
// @ts-ignore
        if(typeof res.then === 'function') res = await res
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          ...res,
// @ts-ignore
          props: {
// @ts-ignore
            ...(res.props || {}),
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/services/web-hosting',
// @ts-ignore
              loaderName: 'getServerSideProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  